import { Flex } from '@chakra-ui/react';
import React, { useState } from 'react';

import { useTextContext } from 'Contexts/textContext';
import { Button, FadeUp, H6, Paragraph } from 'DesignLibrary/atoms';
import RichText from 'Shared/RichText';
import Text from 'Shared/Text';
import { formatDate } from 'Utils/helpers';

import { Content, EnrollBar, OpenEnrollmentBarProps } from './styled';

export const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

export const OpenEnrollmentBar2025 = ({
  isNextClicked,
  openEnrollmentStartDate,
  openEnrollmentEndDate,
}: OpenEnrollmentBarProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const { locale, retrieveContentfulData } = useTextContext();

  if (!openEnrollmentStartDate || !openEnrollmentEndDate) {
    return null;
  }

  // Dates
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(`${openEnrollmentStartDate.substring(0, 10)}T00:00:00.000`);
  const end = new Date(`${openEnrollmentEndDate.substring(0, 10)}T00:00:00.000`);

  // Date Math
  const totalDaysInOE = Math.ceil((end.getTime() - start.getTime()) / ONE_DAY_IN_MS);
  const daysUntilOEStart = Math.ceil((start.getTime() - today.getTime()) / ONE_DAY_IN_MS);

  // on last day, daysUntilOEEnd === 1
  const daysUntilOEEnd = Math.ceil((end.getTime() - today.getTime()) / ONE_DAY_IN_MS);

  const isBeforeOE = daysUntilOEStart > 0;
  const isAfterOE = daysUntilOEEnd <= 0;

  if (isAfterOE) {
    return null;
  }

  const percentComplete = Math.max(((totalDaysInOE - daysUntilOEEnd) / totalDaysInOE) * 100, 0);

  const dataTestId = isBeforeOE ? 'before-oe-content' : 'during-oe-content';

  const endFormatted = formatDate(end, locale || 'en-US', {
    month: 'long',
    day: 'numeric',
  });

  // The "enroll by" label can embed the date via a {date} placeholder, which keeps the
  // surrounding copy and punctuation (e.g. Spanish "Inscríbase para el {date}.") in
  // Contentful rather than in code. A label without the placeholder gets the date
  // appended after it instead. Guard the type: a customer could configure this field as
  // rich text, in which case it isn't a string (and `<Text>` renders nothing anyway).
  const enrollByText = retrieveContentfulData<unknown>(
    'unified_landing_page.open_enrollment_bar_enroll_by_text',
    '',
  );
  const enrollByHasDatePlaceholder = typeof enrollByText === 'string' && enrollByText.includes('{date}');

  // The number is wrapped in its own element so the enlarged style targets it directly
  // (see styled.tsx), not the first bold word — which, in Spanish, is "días".
  const renderDaysCount = (count: number) => <span className="days-count">{count}</span>;

  const getDaysLeftText = () => {
    if (isBeforeOE) {
      return (
        <Paragraph size="small" className="days-left" data-testid="before-oe-text">
          <RichText
            inlineParagraphs
            vars={[renderDaysCount(daysUntilOEStart)]}
            field="unified_landing_page.open_enrollment_bar_before_oe_text"
          />
        </Paragraph>
      );
    }

    if (percentComplete >= 75) {
      return (
        <Paragraph size="small" className="days-left" data-testid="ending-soon-text">
          <RichText
            inlineParagraphs
            vars={[renderDaysCount(daysUntilOEEnd)]}
            field="unified_landing_page.open_enrollment_bar_days_left_ending_soon_text"
          />
        </Paragraph>
      );
    }

    return (
      <Paragraph size="small" className="days-left" data-testid="days-left-text">
        <RichText
          inlineParagraphs
          vars={[renderDaysCount(daysUntilOEEnd)]}
          field="unified_landing_page.open_enrollment_bar_days_left_text"
        />
      </Paragraph>
    );
  };

  return (
    <EnrollBar data-testid="enroll-bar">
      <FadeUp isOpen={isOpen} delay={2000} timeout={1000}>
        <Content
          isNextClicked={isNextClicked}
          openEnrollmentStartDate={openEnrollmentStartDate}
          openEnrollmentEndDate={openEnrollmentEndDate}
          data-testid={dataTestId}
        >
          <Flex className="title" align="center" justify="space-between">
            <Flex align="center" gap="2">
              {getDaysLeftText()}
            </Flex>

            <Button
              onClick={() => setIsOpen(false)}
              size="xsmall"
              buttonType="standalone"
              iconLeft="X"
              iconProps={{
                color: '--text-gray-placeholder',
              }}
            />
          </Flex>
          <div className="bar-wrapper" aria-hidden="true">
            <div className="dates">
              <H6 as="span">
                <Text
                  field="unified_landing_page.open_enrollment_bar_enroll_by_text"
                  vars={{ date: endFormatted }}
                />
                {!enrollByHasDatePlaceholder && <span>{endFormatted}</span>}
              </H6>
            </div>
          </div>
        </Content>
      </FadeUp>
    </EnrollBar>
  );
};
